import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Upload, Crop } from 'lucide-react';
import { getCompanyLogoUrl } from '../utils/companyLogo';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Get full URL for display (prepend API base if relative)
 */
function getFullUrl(url) {
  if (!url) return null;
  if (url.startsWith('/') && !url.startsWith('http')) {
    return `${API_BASE}${url}`;
  }
  return url;
}

const CROP_FRAME_SIZE = 320;
const OUTPUT_LOGO_SIZE = 512;

function getContainedImageSize(image, frameSize) {
  const imageRatio = image.naturalWidth / image.naturalHeight;
  if (imageRatio >= 1) {
    return {
      width: frameSize,
      height: frameSize / imageRatio,
    };
  }

  return {
    width: frameSize * imageRatio,
    height: frameSize,
  };
}

/**
 * Create a square logo blob from the visible crop frame.
 */
function getEditedLogoImg(image, position, zoom) {
  if (!image?.naturalWidth || !image?.naturalHeight) return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const scale = OUTPUT_LOGO_SIZE / CROP_FRAME_SIZE;
  const containedSize = getContainedImageSize(image, CROP_FRAME_SIZE);
  const renderedWidth = containedSize.width * zoom;
  const renderedHeight = containedSize.height * zoom;

  canvas.width = OUTPUT_LOGO_SIZE;
  canvas.height = OUTPUT_LOGO_SIZE;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(
    image,
    (CROP_FRAME_SIZE / 2 + position.x - renderedWidth / 2) * scale,
    (CROP_FRAME_SIZE / 2 + position.y - renderedHeight / 2) * scale,
    renderedWidth * scale,
    renderedHeight * scale
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png', 1);
  });
}

const CompanyLogoEditor = ({ profileData, uploadingLogo, onFileSelect, fileInputRef, compact = false }) => {
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [logoPosition, setLogoPosition] = useState({ x: 0, y: 0 });
  const [logoZoom, setLogoZoom] = useState(1);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const imgRef = useRef(null);
  const internalInputRef = useRef(null);
  const dragStartRef = useRef({ pointerX: 0, pointerY: 0, x: 0, y: 0 });
  const logoInputRef = fileInputRef || internalInputRef;

  const logoUrl = getCompanyLogoUrl(profileData);
  const hasLogo = !!logoUrl;
  const displayUrl = getFullUrl(logoUrl) || logoUrl;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onFileSelect?.(file);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const resetCropControls = () => {
    setLogoPosition({ x: 0, y: 0 });
    setLogoZoom(1);
  };

  const openCropDialog = () => {
    resetCropControls();
    setShowCropDialog(true);
  };

  const closeCropDialog = () => {
    setIsDraggingLogo(false);
    setShowCropDialog(false);
  };

  const handleLogoPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStartRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      x: logoPosition.x,
      y: logoPosition.y,
    };
    setIsDraggingLogo(true);
  };

  const handleLogoPointerMove = (e) => {
    if (!isDraggingLogo) return;
    const dragStart = dragStartRef.current;
    setLogoPosition({
      x: dragStart.x + e.clientX - dragStart.pointerX,
      y: dragStart.y + e.clientY - dragStart.pointerY,
    });
  };

  const handleLogoPointerEnd = (e) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setIsDraggingLogo(false);
  };

  const handleCropComplete = () => {
    if (!imgRef.current) return;
    getEditedLogoImg(imgRef.current, logoPosition, logoZoom).then((blob) => {
      if (blob) {
        const file = new File([blob], 'cropped-logo.png', { type: 'image/png' });
        onFileSelect?.(file);
      }
      closeCropDialog();
    });
  };

  const uploadButton = (
    <Button
      type="button"
      size="sm"
      className={compact ? "bg-blue-600 hover:bg-blue-700 text-white border-0" : ""}
      variant={compact ? undefined : "outline"}
      onClick={() => logoInputRef.current?.click()}
      disabled={uploadingLogo}
    >
      <Upload size={14} className="mr-2" />
      {uploadingLogo ? 'Uploading...' : 'Upload New Logo'}
    </Button>
  );

  const cropButton = hasLogo && (
    <Button
      type="button"
      size="sm"
      className={compact ? "bg-emerald-600 hover:bg-emerald-700 text-white border-0" : ""}
      variant={compact ? undefined : "outline"}
      onClick={openCropDialog}
      disabled={uploadingLogo}
    >
      <Crop size={14} className="mr-2" />
      Edit Existing Logo
    </Button>
  );

  const buttons = (
    <>
      <input
        ref={logoInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={handleFileSelect}
        disabled={uploadingLogo}
      />
      {uploadButton}
      {cropButton}
    </>
  );

  return (
    <div className="space-y-2">
      {compact ? (
        <div className="flex flex-row flex-wrap items-center justify-center gap-2">
          <input
            ref={logoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={handleFileSelect}
            disabled={uploadingLogo}
          />
          {uploadButton}
          {cropButton}
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <img
            src={getFullUrl(getCompanyLogoUrl(profileData)) || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(profileData?.company_name)}`}
            alt="Company logo"
            className="w-20 h-20 rounded-lg object-cover border"
            onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(profileData?.company_name)}`; }}
          />
          <div className="flex flex-row flex-wrap gap-2">
            {buttons}
          </div>
        </div>
      )}

      <Dialog
        open={showCropDialog}
        onOpenChange={(open) => {
          if (open) {
            openCropDialog();
          } else {
            closeCropDialog();
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Crop Company Logo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Drag the logo to reposition it. Use zoom to fit the logo inside the square crop area.</p>
            <div className="flex justify-center">
              {displayUrl && (
                <div
                  className="relative overflow-hidden border-2 border-blue-600 bg-gray-100"
                  style={{ width: CROP_FRAME_SIZE, height: CROP_FRAME_SIZE }}
                  onPointerDown={handleLogoPointerDown}
                  onPointerMove={handleLogoPointerMove}
                  onPointerUp={handleLogoPointerEnd}
                  onPointerCancel={handleLogoPointerEnd}
                >
                  <img
                    ref={imgRef}
                    src={displayUrl}
                    alt="Crop preview"
                    className={`absolute left-1/2 top-1/2 max-w-none select-none ${isDraggingLogo ? 'cursor-grabbing' : 'cursor-grab'}`}
                    style={{
                      maxWidth: CROP_FRAME_SIZE,
                      maxHeight: CROP_FRAME_SIZE,
                      transform: `translate(-50%, -50%) translate(${logoPosition.x}px, ${logoPosition.y}px) scale(${logoZoom})`,
                      transformOrigin: 'center',
                      touchAction: 'none',
                    }}
                    crossOrigin="anonymous"
                    draggable={false}
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <label htmlFor="logoZoom" className="font-medium text-gray-700">Zoom</label>
                <span className="text-gray-500">{Math.round(logoZoom * 100)}%</span>
              </div>
              <input
                id="logoZoom"
                type="range"
                min="0.25"
                max="3"
                step="0.01"
                value={logoZoom}
                onChange={(e) => setLogoZoom(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={resetCropControls}>
                  Reset Position
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCropDialog}>Cancel</Button>
            <Button onClick={handleCropComplete}>Apply Crop</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyLogoEditor;
